import { formatFinanceDate } from "@/utils/format-finance-date";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Column,
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table";
import { ArrowUpDown, ChevronDown } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Label,
  TableCell,
  TableHeader,
  TableRow,
  TableHead,
  Table,
  TableBody,
  Badge,
  Button,
} from "@/components/ui";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { FormFinanceEntry } from "@/components/common/forms/finance-entries";
import { defaultEntriesCategories } from "@/helpers";
import { useDeferredWriteOff } from "@/hooks/entry/use-deferred-write-off";
import { FinanceEntryResponse } from "@/types";
import { formatValue } from "@/utils";

import { Pagination } from "../../pagination";
import { EntryRowActions, type EntryRowActionsEntry } from "./entry-row-actions";
import { EntryStatusPill } from "./entry-status-pill";
import { isPaidStatus } from "./entry-status";

export type ColunsReleaseProps = {
  id: string;
  description: string;
  user: string | null;
  category: string;
  patient: string;
  procedure: string;
  value: string;
  total: string;
  maturity: string;
  pay: string;
  interest_rate: string;
  fine_for_delay: string;
  operation: string;
  status: string | null;
};

type DataTableEntriesProps = {
  data: FinanceEntryResponse;
  per_page: number;
  handlePerPageChange: (newPerPage: number) => void;
};

function sortableHeader(label: string) {
  return function SortableHeader({
    column,
  }: {
    column: Column<ColunsReleaseProps, unknown>;
  }) {
    return (
      <Button
        variant="ghost"
        className="px-0"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        {label}
        <ArrowUpDown className="ml-2 h-4 w-4" />
      </Button>
    );
  };
}

export function DataTableEntries({
  data: { items, data },
  per_page,
  handlePerPageChange,
}: DataTableEntriesProps) {
  const { t } = useTranslation();

  const { scheduleWriteOff, isPaidLocally } = useDeferredWriteOff();

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});
  const [editingId, setEditingId] = useState<string | null>(null);

  const dataMemo = useMemo<ColunsReleaseProps[]>(
    () =>
      items.map((item) => ({
        id: item.id,
        description: item.name ?? "Lançamento sem descrição",
        user: item.user.name ?? "N/A",
        category: item.category.name ?? "N/A",
        patient: item.patient.name ?? "N/A",
        procedure: item.procedure.name ?? "N/A",
        value: item.value.toString(),
        total: item.total.toString(),
        maturity: String(item.date),
        operation: item.type ?? "N/A",
        status: item.status,
        pay: item.pay ?? "N/A",
        fine_for_delay: item.fine_for_delay ?? "N/A",
        interest_rate: item.interest_rate ?? "N/A",
      })),
    [items]
  );

  const handleEdit = useCallback((id: string) => setEditingId(id), []);

  const handleWriteOff = useCallback(
    (entry: EntryRowActionsEntry) => {
      scheduleWriteOff({ id: entry.id, name: entry.name, total: entry.total });
    },
    [scheduleWriteOff]
  );

  const columnsMemo = useMemo<ColumnDef<ColunsReleaseProps>[]>(
    () => [
      {
        accessorKey: "description",
        header: sortableHeader(t("LABEL.description")),
        cell: ({ row }) => (
          <button
            type="button"
            className="min-h-10 text-left font-medium text-brand underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            onClick={() => handleEdit(row.original.id)}
          >
            {row.getValue("description")}
          </button>
        ),
      },
      {
        accessorKey: "user",
        header: sortableHeader(t("LABEL.user")),
        cell: ({ row }) => <div className="capitalize">{row.getValue("user")}</div>,
      },
      {
        accessorKey: "category",
        header: sortableHeader(t("LABEL.category")),
        cell: ({ row }) => {
          const category = String(row.getValue("category"));
          return (
            <span className="capitalize">
              {defaultEntriesCategories.includes(category.toLowerCase())
                ? t(`LABEL.${category.toLowerCase()}`)
                : category}
            </span>
          );
        },
      },
      {
        accessorKey: "patient",
        header: sortableHeader(t("LABEL.patient")),
        cell: ({ row }) => (
          <div className="capitalize">{row.getValue("patient")}</div>
        ),
      },
      {
        accessorKey: "procedure",
        header: sortableHeader(t("LABEL.procedure")),
        cell: ({ row }) => (
          <div className="capitalize">{row.getValue("procedure")}</div>
        ),
      },
      {
        accessorKey: "value",
        header: sortableHeader(t("LABEL.value")),
        cell: ({ row }) => (
          <div className="tabular-nums">
            {formatValue(Number(row.getValue("value")))}
          </div>
        ),
      },
      {
        accessorKey: "total",
        header: sortableHeader("Total"),
        cell: ({ row }) => {
          const breakdown = `Valor inicial ${formatValue(
            Number(row.getValue("value"))
          )} + multa ${formatValue(
            Number(row.original.fine_for_delay)
          )} + juros diários ${formatValue(Number(row.original.interest_rate))}`;

          return (
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="tabular-nums underline decoration-dotted underline-offset-4">
                    {formatValue(Number(row.getValue("total")))}
                  </span>
                </TooltipTrigger>
                <TooltipContent>{breakdown}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        },
      },
      {
        accessorKey: "maturity",
        header: sortableHeader(t("LABEL.maturity")),
        cell: ({ row }) => (
          <div className="tabular-nums">
            {formatFinanceDate(row.getValue("maturity"))}
          </div>
        ),
      },
      {
        accessorKey: "operation",
        header: sortableHeader(t("LABEL.operation")),
        cell: ({ row }) =>
          row.getValue("operation") === "income" ? (
            <Badge className="bg-app-success/10 text-app-fg-strong hover:bg-app-success/20 hover:text-app-fg-strong">
              {t("LABEL.income")}
            </Badge>
          ) : (
            <Badge className="bg-app-danger/10 text-app-fg-strong hover:bg-app-danger/20 hover:text-app-fg-strong">
              {t("LABEL.exit")}
            </Badge>
          ),
      },
      {
        accessorKey: "status",
        header: "Status",
        enableHiding: false,
        cell: ({ row }) => {
          const status = isPaidLocally(row.original.id)
            ? new Date().toLocaleDateString("pt-BR")
            : row.original.status;

          return (
            <EntryStatusPill status={status} maturity={row.original.maturity} />
          );
        },
      },
      {
        id: "actions",
        header: t("LABEL.actions"),
        enableHiding: false,
        cell: ({ row }) => (
          <div className="flex items-center justify-center">
            <EntryRowActions
              entry={{
                id: row.original.id,
                name: row.original.description,
                total: formatValue(Number(row.original.total)),
                maturity: formatFinanceDate(row.original.maturity),
                isPaid:
                  isPaidStatus(row.original.status) ||
                  isPaidLocally(row.original.id),
              }}
              onEdit={handleEdit}
              onWriteOff={handleWriteOff}
            />
          </div>
        ),
      },
    ],
    [t, handleEdit, handleWriteOff, isPaidLocally]
  );

  const table = useReactTable({
    data: dataMemo,
    columns: columnsMemo,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
    initialState: {
      pagination: {
        pageSize: per_page,
        pageIndex: 0,
      },
    },
  });

  return (
    <div className="w-full">
      <div className="flex items-center py-4">
        <div className="flex items-center gap-x-2 max-w-sm">
          <Label>{t("LABEL.show")}</Label>
          <select
            onChange={(e) => handlePerPageChange(Number(e.target.value))}
            value={per_page}
            aria-label={t("LABEL.show")}
            className="h-10 rounded-lg border border-app-border bg-app-surface px-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand"
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
          <Label>itens</Label>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="ml-auto">
              {t("LABEL.columns")} <ChevronDown className="ml-2 h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {table
              .getAllColumns()
              .filter((column) => column.getCanHide())
              .map((column) => (
                <DropdownMenuCheckboxItem
                  key={column.id}
                  checked={column.getIsVisible()}
                  onCheckedChange={(value) => {
                    setColumnVisibility((prev) => ({
                      ...prev,
                      [column.id]: !!value,
                    }));
                  }}
                >
                  {t(`LABEL.${column.id.toLowerCase()}`)}
                </DropdownMenuCheckboxItem>
              ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columnsMemo.length}
                  className="h-24 text-center"
                >
                  {t("LABEL.no results")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <Pagination data={data} />

      <FormFinanceEntry
        id={editingId ?? undefined}
        open={editingId !== null}
        onOpenChange={(open) => {
          if (!open) setEditingId(null);
        }}
      />
    </div>
  );
}
